import React, { useState, useEffect } from 'react';
import { Save, CheckCircle, AlertCircle, Loader } from 'lucide-react';
import { useFormContext } from '../../context/FormContext';
import useAutoSave from '../../hooks/useAutoSave';
import { formatDistanceToNow } from 'date-fns';
import { zhCN } from 'date-fns/locale';

const SaveButton = () => {
  const { assessmentId, hasUnsavedChanges, lastSaved } = useFormContext();
  const { manualSave } = useAutoSave(assessmentId);
  const [saveState, setSaveState] = useState('idle'); // idle, saving, success, error
  const [showLastSaved, setShowLastSaved] = useState(false);

  // Reset success/error states after 3 seconds
  useEffect(() => {
    let timer;
    if (saveState === 'success' || saveState === 'error') {
      timer = setTimeout(() => {
        setSaveState('idle');
      }, 3000);
    }
    return () => clearTimeout(timer);
  }, [saveState]);

  const handleSave = async () => {
    if (saveState === 'saving' || !assessmentId) return;
    
    setSaveState('saving');
    try {
      const success = await manualSave();
      setSaveState(success ? 'success' : 'error');
    } catch (error) {
      console.error('Save failed:', error);
      setSaveState('error');
    }
  };

  // Format last saved time
  const getLastSavedText = () => {
    if (!lastSaved) return '尚未保存';
    
    try {
      return `上次保存: ${formatDistanceToNow(lastSaved, { addSuffix: true, locale: zhCN })}`;
    } catch (error) {
      return '上次保存: 刚才';
    }
  };

  // Button content based on save state
  const renderButtonContent = () => {
    switch (saveState) {
      case 'saving':
        return (
          <>
            <Loader size={16} className="animate-spin" />
            <span>保存中...</span>
          </>
        );
      case 'success':
        return (
          <>
            <CheckCircle size={16} className="text-green-500" />
            <span>已保存</span>
          </>
        );
      case 'error':
        return (
          <>
            <AlertCircle size={16} className="text-red-500" />
            <span>保存失败</span>
          </>
        );
      default:
        return (
          <>
            <Save size={16} />
            <span>保存进度</span>
          </>
        );
    }
  };

  // Button style based on save state
  const getButtonStyles = () => {
    const baseStyles = "flex items-center gap-2 font-medium text-sm border rounded-full px-4 py-2 transition-all duration-200 focus:outline-none";
    
    switch (saveState) {
      case 'saving':
        return `${baseStyles} bg-blue-50 text-blue-600 border-blue-200 cursor-not-allowed`;
      case 'success':
        return `${baseStyles} bg-green-50 text-green-600 border-green-200`;
      case 'error':
        return `${baseStyles} bg-red-50 text-red-600 border-red-200`;
      default:
        return `${baseStyles} ${hasUnsavedChanges 
          ? 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700'
          : 'bg-white text-blue-600 border-blue-200 hover:bg-blue-50 hover:border-blue-300 hover:shadow-sm'
        }`;
    }
  };

  if (!assessmentId) return null;

  return (
    <div className="relative">
      <button 
        onClick={handleSave}
        disabled={saveState === 'saving'}
        className={getButtonStyles()}
        onMouseEnter={() => setShowLastSaved(true)}
        onMouseLeave={() => setShowLastSaved(false)}
      >
        {renderButtonContent()}
      </button>

      {/* Last saved tooltip */}
      {showLastSaved && lastSaved && (
        <div className="absolute top-full right-0 mt-1 bg-gray-800 text-white text-xs rounded py-1 px-2 whitespace-nowrap z-10">
          {getLastSavedText()}
        </div>
      )}
    </div>
  );
};

export default SaveButton;